import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import {
  getNotificacionesConfig,
  updateNotificacionesConfig,
  testEmailNotificaciones,
  getNotificacionesLog,
} from "../../services/api";

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
        checked ? "bg-indigo-500" : "bg-slate-600"
      } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-300 mb-1">{label}</label>
      {hint && <p className="text-xs text-slate-500 mb-2">{hint}</p>}
      {children}
    </div>
  );
}

const inputCls =
  "w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500 transition";

export function NotificacionesSection({ primaryColor }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [showLog, setShowLog] = useState(false);

  useEffect(() => {
    getNotificacionesConfig()
      .then(setConfig)
      .catch(() => toast.error("Error cargando configuración"))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateNotificacionesConfig(config);
      toast.success("Configuración guardada");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await testEmailNotificaciones();
      if (res.success) toast.success(res.mensaje);
      else toast.error(res.error || "Error enviando email de prueba");
    } catch {
      toast.error("Error al enviar prueba");
    } finally {
      setTesting(false);
    }
  };

  const handleLoadLog = async () => {
    setLogLoading(true);
    setShowLog(true);
    try {
      const data = await getNotificacionesLog();
      setLog(data);
    } catch {
      toast.error("Error cargando historial");
    } finally {
      setLogLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-700 bg-slate-800/40 p-8 text-center text-slate-400">
        Cargando...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Canales ─────────────────────────────────────────── */}
      <div className="rounded-2xl border border-slate-700 bg-slate-800/40 p-6 space-y-5">
        <h2 className="text-white font-semibold text-lg">Canales activos</h2>

        <div className="flex items-center justify-between py-3 border-b border-slate-700">
          <div>
            <p className="text-white font-medium text-sm">Email</p>
            <p className="text-slate-400 text-xs">Notificaciones automáticas por correo al alumno</p>
          </div>
          <Toggle
            checked={config.notificaciones_email_habilitado}
            onChange={(v) => setConfig({ ...config, notificaciones_email_habilitado: v })}
          />
        </div>

        <div className="flex items-center justify-between py-3">
          <div>
            <p className="text-white font-medium text-sm">WhatsApp</p>
            <p className="text-slate-400 text-xs">
              Requiere el servicio Baileys configurado · ver documentación
            </p>
          </div>
          <Toggle
            checked={config.notificaciones_whatsapp_habilitado}
            onChange={(v) => setConfig({ ...config, notificaciones_whatsapp_habilitado: v })}
          />
        </div>

        <p className="text-slate-500 text-xs bg-slate-900/60 rounded-lg p-3">
          📅 Las notificaciones se envían automáticamente una vez al día.<br />
          • <strong>3 días antes</strong> del vencimiento<br />
          • <strong>El día</strong> que vence la suscripción
        </p>
      </div>

      {/* ── Config SMTP ──────────────────────────────────────── */}
      <div className="rounded-2xl border border-slate-700 bg-slate-800/40 p-6 space-y-4">
        <div>
          <h2 className="text-white font-semibold text-lg">Configuración SMTP (Email)</h2>
          <p className="text-slate-400 text-xs mt-1">
            Si no configurás SMTP propio, los emails saldrán desde el servidor central de FitAdmin
            pero con el nombre de tu gimnasio.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Nombre del remitente" hint="Lo que verá el alumno (ej: Gimnasio Power)">
            <input
              className={inputCls}
              value={config.smtp_from_name}
              onChange={(e) => setConfig({ ...config, smtp_from_name: e.target.value })}
              placeholder="Nombre de tu gimnasio"
            />
          </Field>
          <Field label="Email / Usuario SMTP">
            <input
              className={inputCls}
              type="email"
              value={config.smtp_user}
              onChange={(e) => setConfig({ ...config, smtp_user: e.target.value })}
              placeholder="tugimnasio@gmail.com"
            />
          </Field>
          <Field label="Contraseña SMTP" hint="Gmail: usar 'Contraseña de aplicación'">
            <input
              className={inputCls}
              type="password"
              value={config.smtp_password}
              onChange={(e) => setConfig({ ...config, smtp_password: e.target.value })}
              placeholder="Contraseña o App Password"
            />
          </Field>
          <Field label="Servidor SMTP">
            <input
              className={inputCls}
              value={config.smtp_host}
              onChange={(e) => setConfig({ ...config, smtp_host: e.target.value })}
              placeholder="smtp.gmail.com"
            />
          </Field>
          <Field label="Puerto">
            <input
              className={inputCls}
              type="number"
              value={config.smtp_port}
              onChange={(e) => setConfig({ ...config, smtp_port: Number(e.target.value) })}
            />
          </Field>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 rounded-xl text-sm font-semibold text-white transition disabled:opacity-50"
            style={{ background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}cc)` }}
          >
            {saving ? "Guardando…" : "Guardar configuración"}
          </button>
          <button
            onClick={handleTest}
            disabled={testing || !config.smtp_user}
            className="px-5 py-2 rounded-xl text-sm font-semibold border border-slate-600 text-slate-300 hover:text-white hover:border-slate-400 transition disabled:opacity-40"
          >
            {testing ? "Enviando…" : "📧 Enviar email de prueba"}
          </button>
        </div>
      </div>

      {/* ── Log ─────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-slate-700 bg-slate-800/40 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-semibold text-lg">Historial de notificaciones</h2>
          <button
            onClick={handleLoadLog}
            disabled={logLoading}
            className="text-sm text-indigo-400 hover:text-indigo-300 transition"
          >
            {logLoading ? "Cargando…" : showLog ? "Recargar" : "Ver historial"}
          </button>
        </div>

        {showLog && (
          <>
            {log.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-6">Sin notificaciones enviadas aún</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-400 border-b border-slate-700">
                      <th className="text-left py-2 pr-4 font-medium">Alumno</th>
                      <th className="text-left py-2 pr-4 font-medium">Tipo</th>
                      <th className="text-left py-2 pr-4 font-medium">Canal</th>
                      <th className="text-left py-2 pr-4 font-medium">Estado</th>
                      <th className="text-left py-2 font-medium">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {log.map((r) => (
                      <tr key={r.id} className="border-b border-slate-800 hover:bg-slate-800/30">
                        <td className="py-2 pr-4 text-white">{r.usuario}</td>
                        <td className="py-2 pr-4 text-slate-300">{r.tipo_display}</td>
                        <td className="py-2 pr-4">
                          <span className="bg-slate-700 text-slate-300 px-2 py-0.5 rounded-full text-xs capitalize">
                            {r.canal}
                          </span>
                        </td>
                        <td className="py-2 pr-4">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              r.estado === "enviado"
                                ? "bg-green-500/15 text-green-400"
                                : "bg-red-500/15 text-red-400"
                            }`}
                          >
                            {r.estado === "enviado" ? "✓ Enviado" : "✗ Error"}
                          </span>
                          {r.estado === "error" && r.detalle && (
                            <span className="ml-2 text-xs text-red-400/70">{r.detalle.slice(0, 60)}</span>
                          )}
                        </td>
                        <td className="py-2 text-slate-500 text-xs">
                          {new Date(r.fecha).toLocaleString("es-AR", {
                            day: "2-digit", month: "2-digit", year: "2-digit",
                            hour: "2-digit", minute: "2-digit",
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
